import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanAdminRoomList } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const ROOM_PAGE_SIZE = 30;
const roomSortOptions = [
  { value: 'facility-asc', label: '施設昇順' },
  { value: 'facility-desc', label: '施設降順' },
  { value: 'room-asc', label: '部屋名昇順' },
  { value: 'room-desc', label: '部屋名降順' },
  { value: 'active-status', label: '使用状態順' },
  { value: 'capacity-desc', label: '定員が多い順' },
  { value: 'updated-desc', label: '更新日が新しい順' },
];

const roomColumnStyle = {
  width: '12rem',
  minWidth: '12rem',
};

const facilityColumnStyle = {
  width: '18rem',
  minWidth: '18rem',
};

const compactColumnStyle = {
  width: '7rem',
  minWidth: '7rem',
};
const updatedAtColumnStyle = {
  width: '11.5rem',
  minWidth: '11.5rem',
  whiteSpace: 'nowrap',
};
const actionColumnStyle = {
  width: '9rem',
  minWidth: '9rem',
  whiteSpace: 'nowrap',
};
const filterNoticeStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.65rem',
  marginBottom: '1rem',
  padding: '0.75rem 1rem',
  border: '1px solid rgba(3, 152, 253, 0.18)',
  borderRadius: '0.5rem',
  background: 'rgba(3, 152, 253, 0.06)',
  color: '#24303a',
};
const filterNoticeTextStyle = {
  margin: 0,
  fontSize: '0.9rem',
  fontWeight: 600,
};
const filterNoticeLinkStyle = {
  fontSize: '0.85rem',
  fontWeight: 600,
};

const compareText = (left = '', right = '') => String(left || '').localeCompare(String(right || ''), 'ja');

const comparableDateTime = (value = '') => {
  const timestamp = Date.parse(String(value || '').replace(' ', 'T'));
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortRoomRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'active-status') {
      if (left.isActive === right.isActive) {
        return compareText(left.facilityName, right.facilityName);
      }
      return left.isActive ? -1 : 1;
    }

    if (sortKey === 'facility-desc') {
      return compareText(right.facilityName, left.facilityName);
    }

    if (sortKey === 'room-asc') {
      return compareText(left.roomName, right.roomName);
    }

    if (sortKey === 'room-desc') {
      return compareText(right.roomName, left.roomName);
    }

    if (sortKey === 'capacity-desc') {
      return right.capacity - left.capacity;
    }

    if (sortKey === 'updated-desc') {
      return comparableDateTime(right.updatedAt) - comparableDateTime(left.updatedAt);
    }

    return compareText(left.facilityName, right.facilityName);
  });

  return nextRows;
};

const statusToUi = (isActive) => ({
  label: isActive ? '使用中' : '停止中',
  tone: isActive ? 'published' : 'draft',
});

const AdminRoomsListPage = () => {
  const content = yowanPageContent['/admin/events/rooms'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const facilityId = searchParams.get('facilityId') || '';
  const facilityLabel = searchParams.get('facilityLabel') || '';
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('facility-asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    let isMounted = true;

    const loadRooms = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const payload = await fetchYowanAdminRoomList({
          page: currentPage,
          pageSize: ROOM_PAGE_SIZE,
          q: searchQuery.trim(),
          facilityId,
        });

        if (!isMounted) {
          return;
        }

        setRows(payload.results);
        setApiCount(payload.count);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRows([]);
        setApiCount(0);
        setLoadError('部屋情報を読み込めませんでした。');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadRooms();

    return () => {
      isMounted = false;
    };
  }, [content, currentPage, searchQuery, facilityId]);

  if (!content) {
    return null;
  }

  const sortedRows = sortRoomRows(rows, sortKey);
  const totalPages = Math.max(1, Math.ceil(apiCount / ROOM_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * ROOM_PAGE_SIZE;
  const visibleRows = sortedRows;

  return (
    <AdminBlogShell activeNavHref="/admin/events/rooms" routeKey="/admin/events/rooms">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={
          content.primaryAction
            ? [
                <Link
                  key="primary-action"
                  className="c-form__submit"
                  to={appendYowanPreviewQueryIfNeeded(content.primaryAction.href, preservedDomainSearch)}
                >
                  {content.primaryAction.label}
                </Link>,
              ]
            : []
        }
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        metaText={`${apiCount}件中 ${visibleRows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
        searchPlaceholder="部屋名 / 施設 / 定員 / 使用状態"
        sortOptions={roomSortOptions}
      />
      {facilityId ? (
        <div className="c-surface-content" style={filterNoticeStyle}>
          <p style={filterNoticeTextStyle}>
            対象施設: {facilityLabel || '選択中の施設'}
          </p>
          <Link
            style={filterNoticeLinkStyle}
            to={appendYowanPreviewQueryIfNeeded('/admin/events/rooms', preservedDomainSearch)}
          >
            絞り込みを解除
          </Link>
        </div>
      ) : null}
      <AdminRelatedLinks links={content.relatedLinks} preservedDomainSearch={preservedDomainSearch} />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col" style={roomColumnStyle}>部屋</th>
              <th scope="col" style={facilityColumnStyle}>施設</th>
              <th scope="col" style={compactColumnStyle}>定員目安</th>
              <th scope="col">使用状態</th>
              <th scope="col" style={updatedAtColumnStyle}>更新日</th>
              <th scope="col" style={actionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => {
              const statusUi = statusToUi(row.isActive);

              return (
                <tr key={row.id}>
                  <td>{startIndex + index + 1}</td>
                  <td style={roomColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.roomName}</p>
                  </td>
                  <td style={facilityColumnStyle}>{row.facilityName}</td>
                  <td style={compactColumnStyle}>{row.capacity}</td>
                  <td>
                    <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${statusUi.tone}`}>
                      {statusUi.label}
                    </span>
                  </td>
                  <td style={updatedAtColumnStyle}>{row.updatedAt || '-'}</td>
                  <td style={actionColumnStyle}>
                    <div className="yowan-admin-resource-table__actions">
                      <Link to={appendYowanPreviewQueryIfNeeded(row.editHref, preservedDomainSearch)}>編集</Link>
                      {row.facilityId ? (
                        <Link to={appendYowanPreviewQueryIfNeeded(`/admin/events/facilities/${row.facilityId}/edit`, preservedDomainSearch)}>施設</Link>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoading && visibleRows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  {loadError || '表示できる部屋がありません。'}
                </td>
              </tr>
            ) : null}
            {isLoading ? (
              <tr>
                <td colSpan={7}>
                  <LoadingSpinner size="sm" />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminRoomsListPage;
